<template>
  <Teleport to="body">
    <transition name="sheet-fade">
      <div v-if="visible" class="moderation-overlay" @click.self="close">
        <transition name="sheet-slide">
          <div v-if="visible" class="moderation-sheet">
            <!-- Header -->
            <div class="sheet-header">
              <div class="sheet-handle"></div>
              <div class="sheet-title">
                {{ $t('chat.chat_moderation') || 'Chat Moderation' }}
              </div>
              <button class="sheet-close" @click="close">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            
            <!-- Actions -->
            <div class="sheet-content">
              <!-- Toggle Chat Lock -->
              <button 
                class="moderation-action"
                :class="{ loading: loadingAction === 'toggle_lock' }"
                :disabled="loadingAction !== null"
                @click="handleToggleLock"
              >
                <div class="action-icon" :class="isChatLocked ? 'icon-unlock' : 'icon-lock'">
                  <i :class="isChatLocked ? 'bi bi-unlock-fill' : 'bi bi-lock-fill'"></i>
                </div>
                <div class="action-content">
                  <span class="action-title">
                    {{ isChatLocked 
                      ? ($t('chat.unlock_chat') || 'Unlock Chat')
                      : ($t('chat.lock_chat') || 'Lock Chat') 
                    }}
                  </span>
                  <span class="action-description">
                    {{ isChatLocked 
                      ? ($t('chat.unlock_chat_description') || 'Allow all members to send messages')
                      : ($t('chat.lock_chat_description') || 'Only you can send messages')
                    }}
                  </span>
                </div>
                <div class="action-arrow">
                  <span v-if="loadingAction === 'toggle_lock'" class="spinner-sm"></span>
                  <i v-else class="bi bi-chevron-left"></i>
                </div>
              </button>
              
              <!-- Kick Student -->
              <button 
                class="moderation-action action-danger"
                :class="{ loading: loadingAction === 'kick' }"
                :disabled="loadingAction !== null"
                @click="showKickDialog"
              >
                <div class="action-icon icon-danger">
                  <i class="bi bi-person-dash-fill"></i>
                </div>
                <div class="action-content">
                  <span class="action-title">
                    {{ $t('chat.kick_student') || 'Remove Member' }}
                  </span>
                  <span class="action-description">
                    {{ $t('chat.kick_student_description') || 'Remove a member from this group' }}
                  </span>
                </div>
                <div class="action-arrow">
                  <span v-if="loadingAction === 'kick'" class="spinner-sm"></span>
                  <i v-else class="bi bi-chevron-left"></i>
                </div>
              </button>
              
              <!-- Add Student -->
              <button 
                class="moderation-action action-primary"
                :class="{ loading: loadingAction === 'add' }"
                :disabled="loadingAction !== null"
                @click="showAddDialog"
              >
                <div class="action-icon icon-primary">
                  <i class="bi bi-person-plus-fill"></i>
                </div>
                <div class="action-content">
                  <span class="action-title">
                    {{ $t('chat.add_student') || 'Add Member' }}
                  </span>
                  <span class="action-description">
                    {{ $t('chat.add_student_description') || 'Add a member back to this group' }}
                  </span>
                </div>
                <div class="action-arrow">
                  <span v-if="loadingAction === 'add'" class="spinner-sm"></span>
                  <i v-else class="bi bi-chevron-left"></i>
                </div>
              </button>
            </div>
          </div>
        </transition>
        
        <!-- Kick User Dialog -->
        <transition name="dialog-fade">
          <div v-if="showKickUserDialog" class="sub-dialog-overlay" @click.self="closeKickDialog">
            <div class="sub-dialog">
              <div class="sub-dialog-header">
                <i class="bi bi-person-dash-fill text-danger"></i>
                <h4>{{ $t('chat.kick_student') || 'Remove Member' }}</h4>
              </div>
              <div class="sub-dialog-content">
                <p>{{ $t('chat.select_member_to_kick') || 'Select a member to remove:' }}</p>
                <div class="members-list">
                  <div 
                    v-for="member in kickableMembers" 
                    :key="member.id"
                    class="member-item"
                    :class="{ selected: selectedKickUser === member.id }"
                    @click="selectedKickUser = member.id"
                  >
                    <div class="member-avatar">
                      <img v-if="member.avatar" :src="member.avatar" @error="onAvatarError" alt="" />
                      <div v-else class="avatar-placeholder">{{ member.initial }}</div>
                    </div>
                    <span class="member-name">{{ member.name }}</span>
                    <i v-if="selectedKickUser === member.id" class="bi bi-check-circle-fill text-success"></i>
                  </div>
                  <div v-if="kickableMembers.length === 0" class="no-members">
                    {{ $t('chat.no_other_participants') || 'No other members to remove' }}
                  </div>
                </div>
              </div>
              <div class="sub-dialog-actions">
                <button class="btn-cancel" @click="closeKickDialog">
                  {{ $t('common.cancel') || 'Cancel' }}
                </button>
                <button 
                  class="btn-danger" 
                  :disabled="!selectedKickUser || loadingAction === 'kick'"
                  @click="confirmKick"
                >
                  <span v-if="loadingAction === 'kick'" class="spinner-sm"></span>
                  <span v-else>{{ $t('chat.kick') || 'Remove' }}</span>
                </button>
              </div>
            </div>
          </div>
        </transition>
        
        <!-- Add User Dialog -->
        <transition name="dialog-fade">
          <div v-if="showAddUserDialog" class="sub-dialog-overlay" @click.self="closeAddDialog">
            <div class="sub-dialog">
              <div class="sub-dialog-header">
                <i class="bi bi-person-plus-fill text-success"></i>
                <h4>{{ $t('chat.add_student') || 'Add Member' }}</h4>
              </div>
              <div class="sub-dialog-content">
                <p>{{ $t('chat.select_friend_to_add') || 'Select a friend to add:' }}</p>
                <div v-if="loadingFriends" class="loading-friends">
                  <div class="spinner"></div>
                  <span>{{ $t('common.loading') || 'Loading...' }}</span>
                </div>
                <div v-else class="members-list">
                  <div 
                    v-for="friend in addableFriends" 
                    :key="friend.id"
                    class="member-item"
                    :class="{ selected: selectedAddUser === friend.id }"
                    @click="selectedAddUser = friend.id"
                  >
                    <div class="member-avatar">
                      <img v-if="friend.avatar" :src="friend.avatar" @error="onAvatarError" alt="" />
                      <div v-else class="avatar-placeholder">{{ friend.initial }}</div>
                    </div>
                    <span class="member-name">{{ friend.name }}</span>
                    <i v-if="selectedAddUser === friend.id" class="bi bi-check-circle-fill text-success"></i>
                  </div>
                  <div v-if="addableFriends.length === 0" class="no-members">
                    {{ $t('chat.no_friends_to_add') || 'No friends available to add' }}
                  </div>
                </div>
              </div>
              <div class="sub-dialog-actions">
                <button class="btn-cancel" @click="closeAddDialog">
                  {{ $t('common.cancel') || 'Cancel' }}
                </button>
                <button 
                  class="btn-success" 
                  :disabled="!selectedAddUser || loadingAction === 'add'"
                  @click="confirmAdd"
                >
                  <span v-if="loadingAction === 'add'" class="spinner-sm"></span>
                  <span v-else>{{ $t('chat.add') || 'Add' }}</span>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { ChatService } from '@/services/chat';
import { chatState } from '@/stores/chat';

export default {
  name: 'ChatModerationSheet',
  
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: String,
      required: true,
    },
    isChatLocked: {
      type: Boolean,
      default: false,
    },
    participants: {
      type: Object,
      default: () => ({}),
    },
  },
  
  emits: ['close', 'action-complete'],
  
  data() {
    return {
      loadingAction: null,
      showKickUserDialog: false,
      showAddUserDialog: false,
      selectedKickUser: null,
      selectedAddUser: null,
      friendsList: [],
      loadingFriends: false,
    };
  },
  
  computed: {
    kickableMembers() {
      const currentUserId = String(chatState.userId);
      return Object.entries(this.participants || {})
        .filter(([id]) => String(id) !== currentUserId)
        .map(([id, data]) => ({
          id,
          name: data.name || data.username || `User ${id}`,
          avatar: data.avatar || data.photoUrl,
          initial: (data.name || data.username || 'U').charAt(0).toUpperCase(),
        }));
    },
    
    addableFriends() {
      const participantIds = new Set(Object.keys(this.participants || {}).map(String));
      return this.friendsList
        .filter(f => !participantIds.has(String(f.id)))
        .map(f => ({
          id: f.id,
          name: f.name || f.username || `User ${f.id}`,
          avatar: f.avatar || f.photoUrl,
          initial: (f.name || f.username || 'U').charAt(0).toUpperCase(),
        }));
    },
  },
  
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
        this.resetDialogs();
      }
    },
  },
  
  methods: {
    async handleToggleLock() {
      this.loadingAction = 'toggle_lock';
      
      try {
        const result = await ChatService.toggleChatLock(this.conversationId);
        
        if (result.success) {
          this.$emit('action-complete', {
            action: 'toggle_lock',
            isLocked: !this.isChatLocked,
            message: result.message,
          });
          this.close();
        } else {
          console.error('[ChatModerationSheet] Toggle lock failed:', result.message);
        }
      } catch (e) {
        console.error('[ChatModerationSheet] Toggle lock error:', e);
      } finally {
        this.loadingAction = null;
      }
    },
    
    showKickDialog() {
      this.showKickUserDialog = true;
      this.selectedKickUser = null;
    },
    
    closeKickDialog() {
      this.showKickUserDialog = false;
      this.selectedKickUser = null;
    },
    
    async confirmKick() {
      if (!this.selectedKickUser) return;
      
      this.loadingAction = 'kick';
      
      try {
        const result = await ChatService.kickUser(this.conversationId, this.selectedKickUser);
        
        if (result.success) {
          this.$emit('action-complete', {
            action: 'kick',
            userId: this.selectedKickUser,
            message: result.message,
          });
          this.closeKickDialog();
          this.close();
        } else {
          console.error('[ChatModerationSheet] Kick failed:', result.message);
        }
      } catch (e) {
        console.error('[ChatModerationSheet] Kick error:', e);
      } finally {
        this.loadingAction = null;
      }
    },
    
    async showAddDialog() {
      this.showAddUserDialog = true;
      this.selectedAddUser = null;
      
      // Load friends list
      this.loadingFriends = true;
      try {
        this.friendsList = await ChatService.getFriendsList();
      } catch (e) {
        console.error('[ChatModerationSheet] Failed to load friends:', e);
        this.friendsList = [];
      } finally {
        this.loadingFriends = false;
      }
    },
    
    closeAddDialog() {
      this.showAddUserDialog = false;
      this.selectedAddUser = null;
    },
    
    async confirmAdd() {
      if (!this.selectedAddUser) return;
      
      this.loadingAction = 'add';
      
      try {
        const result = await ChatService.addUserToGroup(this.conversationId, this.selectedAddUser);
        
        if (result.success) {
          this.$emit('action-complete', {
            action: 'add',
            userId: this.selectedAddUser,
            message: result.message,
          });
          this.closeAddDialog();
          this.close();
        } else {
          console.error('[ChatModerationSheet] Add failed:', result.message);
        }
      } catch (e) {
        console.error('[ChatModerationSheet] Add error:', e);
      } finally {
        this.loadingAction = null;
      }
    },
    
    resetDialogs() {
      this.showKickUserDialog = false;
      this.showAddUserDialog = false;
      this.selectedKickUser = null;
      this.selectedAddUser = null;
      this.loadingAction = null;
    },
    
    onAvatarError(e) {
      e.target.style.display = 'none';
    },
    
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Overlay */
.moderation-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* Sheet */
.moderation-sheet {
  background: var(--bs-body-bg, #fff);
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 500px;
  overflow: hidden;
}

/* Header */
.sheet-header {
  padding: 12px 16px 16px;
  position: relative;
}

.sheet-handle {
  width: 40px;
  height: 4px;
  background: var(--bs-secondary-bg, #e9ecef);
  border-radius: 2px;
  margin: 0 auto 16px;
}

.sheet-title {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: var(--bs-body-color, #333);
}

.sheet-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: var(--bs-tertiary-bg, #f5f5f5);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bs-secondary-color, #6c757d);
  transition: all 0.2s;
}

.sheet-close:hover {
  background: var(--bs-danger, #dc3545);
  color: #fff;
}

/* Content */
.sheet-content {
  padding: 0 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Moderation Action */
.moderation-action {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 2px solid var(--bs-border-color, #e5e5e5);
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
}

.moderation-action:hover:not(:disabled) {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

.moderation-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.moderation-action.action-danger:hover:not(:disabled) {
  border-color: var(--bs-danger, #dc3545);
  background: var(--bs-danger-bg-subtle, #f8d7da);
}

.moderation-action.action-primary:hover:not(:disabled) {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.action-icon.icon-lock {
  background: var(--bs-warning-bg-subtle, #fff3cd);
  color: var(--bs-warning, #ffc107);
}

.action-icon.icon-unlock {
  background: var(--bs-success-bg-subtle, #d1e7dd);
  color: var(--bs-success, #198754);
}

.action-icon.icon-danger {
  background: var(--bs-danger-bg-subtle, #f8d7da);
  color: var(--bs-danger, #dc3545);
}

.action-icon.icon-primary {
  background: var(--bs-success-bg-subtle, #d1e7dd);
  color: var(--bs-success, #198754);
}

.action-content {
  flex: 1;
  min-width: 0;
}

.action-title {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: var(--bs-body-color, #333);
  margin-bottom: 2px;
}

.action-description {
  display: block;
  font-size: 13px;
  color: var(--bs-secondary-color, #6c757d);
}

.action-arrow {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 16px;
}

/* Sub Dialog */
.sub-dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.sub-dialog {
  background: var(--bs-body-bg, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.sub-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-bottom: 1px solid var(--bs-border-color, #e5e5e5);
}

.sub-dialog-header i {
  font-size: 24px;
}

.sub-dialog-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.sub-dialog-content {
  padding: 16px 20px;
  flex: 1;
  overflow-y: auto;
}

.sub-dialog-content p {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--bs-secondary-color, #6c757d);
}

.loading-friends {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  color: var(--bs-secondary-color, #6c757d);
}

.loading-friends .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--bs-border-color, #e5e5e5);
  border-top-color: var(--bs-success, #198754);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.members-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 2px solid var(--bs-border-color, #e5e5e5);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.member-item:hover {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

.member-item.selected {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success-bg-subtle, #d1e7dd);
}

.member-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.member-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bs-success, #198754);
  color: #fff;
  font-weight: 600;
  font-size: 16px;
}

.member-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--bs-body-color, #333);
}

.no-members {
  text-align: center;
  padding: 40px 20px;
  color: var(--bs-secondary-color, #6c757d);
  font-size: 14px;
}

.sub-dialog-actions {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--bs-border-color, #e5e5e5);
}

.btn-cancel,
.btn-danger,
.btn-success {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-cancel {
  background: var(--bs-tertiary-bg, #f5f5f5);
  color: var(--bs-body-color, #333);
}

.btn-cancel:hover {
  background: var(--bs-secondary-bg, #e9ecef);
}

.btn-danger {
  background: var(--bs-danger, #dc3545);
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: var(--bs-danger-dark, #bb2d3b);
}

.btn-success {
  background: var(--bs-success, #198754);
  color: #fff;
}

.btn-success:hover:not(:disabled) {
  background: var(--bs-success-dark, #157347);
}

.btn-danger:disabled,
.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner */
.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Transitions */
.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.3s ease;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.3s ease;
}

.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(100%);
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

/* Dark mode */
[data-bs-theme="dark"] .moderation-sheet,
[data-bs-theme="dark"] .sub-dialog {
  background: var(--bs-body-bg);
}

/* Responsive */
@media (min-width: 768px) {
  .moderation-overlay {
    align-items: center;
  }
  
  .moderation-sheet {
    border-radius: 20px;
  }
}
</style>
